// Renovacion de sesion de las SPAs del pool administrativo
// (docs/PLAN-TORRE-V2.md, seccion 7). Mudado desde
// apps/tower/src/auth/renewal.ts: la logica es la misma, lo unico nuevo es
// que el store de sesion y la config del pool entran por `createRenewal` en
// vez de importarse de la app. Ese era el unico acoplamiento real que tenia
// el modulo con la torre (`readConfig` de apps/tower/src/config.ts).
//
// La pantalla queda abierta el turno entero, asi que cuando al ID token le
// quedan menos de 5 minutos se renueva con el refresh token de Cognito, sin
// pasar por el login. Las capas de datos hacen
//   sesion = await conSesionFresca(sesion, alCerrarSesion)
// antes de cada request.
//
// Ademas la SPA programa la renovacion sola (iniciarRenovacionAutomatica,
// cableada en la raiz): un unico temporizador que despierta justo antes del
// umbral, no un poller. Sin eso el operador cae al login en medio del turno
// aunque el refresh token siga vivo (AT-1).
//
// El refresh token vive SOLO en memoria (session.ts): una sesion
// reconstruida desde el storage tras recargar la pestaña no lo tiene. En ese
// caso no hay nada con que renovar y, llegado el umbral, se cierra la sesion
// para que el operador vuelva al login (downgrade aceptado, auditoria A1).
import { renovarSesion, TOTP_OBLIGATORIO } from "./cognito.js";
import { sessionValid } from "./session.js";
/** Umbral de renovacion: menos de 5 minutos de vida restante del ID token. */
export const UMBRAL_RENOVACION_MS = 5 * 60 * 1000;
/**
 * Espera antes de reintentar cuando la renovacion fallo pero el ID token
 * todavia vale: la red vuelve sola y no hay que quemar el refresh en un loop.
 */
export const REINTENTO_RENOVACION_MS = 30 * 1000;
/** true cuando al ID token le quedan menos de UMBRAL_RENOVACION_MS. */
export function necesitaRenovacion(session, now = Date.now()) {
    return session.expiraEn * 1000 - now < UMBRAL_RENOVACION_MS;
}
/**
 * Milisegundos hasta que `necesitaRenovacion` pase a true (0 si ya lo es).
 * El +1 no es cosmetico: despertar justo en el borde del umbral daria
 * `necesitaRenovacion` false y costaria un ciclo de reintento de mas.
 */
export function msHastaRenovacion(session, now = Date.now()) {
    return Math.max(0, session.expiraEn * 1000 - now - UMBRAL_RENOVACION_MS + 1);
}
function pestanaOculta() {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
}
function alVolverALaVista(escuchar) {
    if (typeof document === "undefined")
        return () => { };
    document.addEventListener("visibilitychange", escuchar);
    return () => {
        document.removeEventListener("visibilitychange", escuchar);
    };
}
export function createRenewal({ store, pool, politica = TOTP_OBLIGATORIO }) {
    const renovadorReal = (session) => {
        const config = pool();
        if (config === null) {
            return Promise.reject(new Error("Config de Cognito ausente: no se puede renovar."));
        }
        return renovarSesion(config, session, politica);
    };
    /**
     * Devuelve una sesion lista para usar en el proximo request:
     * - lejos del vencimiento: la misma sesion, sin tocar nada;
     * - por vencer y con refresh token en memoria: la renueva, la persiste y la
     *   devuelve; si la renovacion falla pero el ID token todavia vale, sigue
     *   con el (error transitorio: se reintenta en el proximo request);
     * - por vencer y sin refresh token en memoria (pestaña recargada): no
     *   intenta renovar, borra la sesion, invoca onLogout y devuelve null;
     * - vencida sin renovacion posible: idem, la UI vuelve al login.
     */
    const conSesionFresca = async (session, onLogout, deps = {}) => {
        const now = deps.now ?? Date.now;
        if (!necesitaRenovacion(session, now()))
            return session;
        const cerrar = () => {
            store.borrar();
            onLogout();
            return null;
        };
        if (session.refreshToken === undefined)
            return cerrar();
        const renovar = deps.renovar ?? renovadorReal;
        try {
            const renovada = await renovar(session);
            store.guardar(renovada);
            return renovada;
        }
        catch {
            // Error transitorio o refresh revocado: mientras el ID token siga
            // vigente se usa; si ya vencio, se cierra la sesion.
        }
        return sessionValid(session, now()) ? session : cerrar();
    };
    /**
     * Programa la renovacion silenciosa de la sesion y devuelve como cancelarla.
     *
     * Un solo temporizador por sesion, rearmado despues de cada renovacion: no
     * es un poller, no hay dos refresh en vuelo a la vez y en una pestaña oculta
     * no se renueva de mas (el temporizador sigue siendo uno solo). Volver a la
     * pestaña no renueva por si mismo: solo dispara el ciclo cuando el token ya
     * entro en el umbral, porque el navegador atrasa los temporizadores de las
     * pestañas de fondo y la renovacion pudo quedar corrida.
     *
     * Sin refresh token en memoria (pestaña recargada) no programa nada: la
     * sesion vive lo que dure el ID token, como hasta ahora.
     *
     * El cancelar devuelto limpia el temporizador y la suscripcion de
     * visibilidad, y descarta una renovacion en vuelo: al desmontar no queda
     * nada colgado ni nadie escribiendo estado de un componente muerto.
     */
    const iniciarRenovacionAutomatica = ({ session, onRenovada, onLogout, deps = {}, }) => {
        if (session.refreshToken === undefined)
            return () => { };
        const now = deps.now ?? Date.now;
        const oculta = deps.oculta ?? pestanaOculta;
        const suscribir = deps.alCambiarVisibilidad ?? alVolverALaVista;
        let actual = session;
        let cancelado = false;
        let enVuelo = false;
        let timer;
        const limpiarTimer = () => {
            if (timer !== undefined)
                clearTimeout(timer);
            timer = undefined;
        };
        const programar = (ms) => {
            if (cancelado)
                return;
            limpiarTimer();
            timer = setTimeout(() => {
                timer = undefined;
                void ciclo();
            }, ms);
        };
        const ciclo = async () => {
            // Una sola renovacion en vuelo: el tick y la vuelta a la pestaña pueden
            // coincidir y no se dispara un segundo refresh.
            if (cancelado || enVuelo)
                return;
            enVuelo = true;
            let fresca;
            try {
                fresca = await conSesionFresca(actual, onLogout, deps);
            }
            finally {
                enVuelo = false;
            }
            if (cancelado)
                return;
            // null: conSesionFresca ya borro la sesion y aviso. No se reprograma.
            if (fresca === null)
                return;
            if (fresca !== actual) {
                actual = fresca;
                onRenovada(fresca);
                programar(msHastaRenovacion(actual, now()));
                return;
            }
            // Misma sesion: o todavia no habia que renovar, o la renovacion fallo y
            // el ID token aguanta. En el segundo caso el umbral ya paso y msHasta da
            // 0, asi que se espera el reintento en vez de reprogramar para ya.
            const proximo = msHastaRenovacion(actual, now());
            programar(proximo === 0 ? REINTENTO_RENOVACION_MS : proximo);
        };
        const desuscribir = suscribir(() => {
            if (cancelado || oculta())
                return;
            if (!necesitaRenovacion(actual, now()))
                return;
            void ciclo();
        });
        programar(msHastaRenovacion(actual, now()));
        return () => {
            cancelado = true;
            limpiarTimer();
            desuscribir();
        };
    };
    return { conSesionFresca, iniciarRenovacionAutomatica };
}
//# sourceMappingURL=renewal.js.map